<svelte:options
  customElement={{
    tag: 'dynamic-svelte-data-portal',
    props: {
      datasource: { type: 'String' },
      isFavourites: { type: 'String' },
    },
    shadow: 'none',
  }}
/>

<script>
  import { DragDropProvider } from '@dnd-kit/svelte';
  import Chart from '../chart/chart.svelte';
  import DateFilterBar from './date-filter-bar.svelte';

  import { createDraggable } from '@dnd-kit/svelte';
  import { createDroppable } from '@dnd-kit/svelte';
  import { move } from '@dnd-kit/helpers';
  import { DragOverlay } from '@dnd-kit/svelte';
  import SortableChart from './sortable-chart.svelte';
  import Datatable from '../datatable/datatable.svelte';

  let { datasource = '/', favourites = false } = $props();

  // svelte-ignore state_referenced_locally
  let isFavourites = $state(favourites === 'true');

  let dateRange = $state({
    min: undefined,
    max: undefined,
  });

  const params = new URLSearchParams(window.location.search);

  let manifest = $state(null);
  let favouriteCategories = $state([]);
  let favouriteCharts = $state({});

  let sharedFavourites = $derived.by(() => deserializeFavourites(params.get('f')));

  function serializeFavourites() {
    const favourites = getFavourites();
    let output = '';
    for (const [categoryId, category] of Object.entries(favourites)) {
      output += categoryId + ':';
      for (const [chartId, chart] of Object.entries(category.charts)) {
        output += `${chartId},${chart.colspan},${chart.rowspan};`;
      }
      output = output.substring(0, output.length - 1);
      output += '|';
    }
    output = output.substring(0, output.length - 1);

    return encodeURIComponent(output);
  }

  function deserializeFavourites(serialized) {
    if (serialized) {
      const categories = serialized.split('|');
      const output = {};
      for (const category of categories) {
        const parts = category.split(':');

        const categoryOut = {
          charts: {},
          dateRange: {},
        };
        output[parts[0]] = categoryOut;

        const charts = parts[1].split(';');
        for (const chart of charts) {
          const chartParts = chart.split(',');

          categoryOut.charts[chartParts[0]] = {
            colspan: chartParts[1],
            rowspan: chartParts[2],
          };
        }
      }

      return output;
    }
    return undefined;
  }

  function getFavourites() {
    if (sharedFavourites) {
      return sharedFavourites;
    } else {
      return JSON.parse(localStorage.getItem('dp-favourites') ?? '{}');
    }
  }

  function updateFavourites(favourites) {
    if (!sharedFavourites) {
      localStorage.setItem('dp-favourites', JSON.stringify(favourites));
    }
  }

  function getFavouriteApiUrl() {
    let url = datasource;
    const favourites = getFavourites();

    const charts = {};
    for (const category of Object.values(favourites)) {
      for (const chartId of Object.keys(category.charts)) {
        charts[chartId] = true;
      }
    }

    url += '?favourites=' + encodeURIComponent(Object.keys(charts).join(','));

    return url;
  }

  function calculateFavourites() {
    const categories = {};
    const favourites = getFavourites();

    for (const [categoryId, category] of Object.entries(favourites)) {
      categories[categoryId] = {
        ...category,
        id: categoryId,
        charts: [],
      };

      for (const [chartId, chart] of Object.entries(category.charts)) {
        favouriteCharts[chartId] = {
          ...chart,
          id: chartId,
          categoryId,
        };

        categories[categoryId].charts.push(favouriteCharts[chartId]);
      }

      if (categories[categoryId].charts.length === 0) {
        delete favourites[categoryId];
        updateFavourites(favourites);
      }
    }

    favouriteCategories = Object.values(categories).filter(
      (category) => category.charts.length > 0
    );
    favouriteCategories.sort((a, b) => a.title?.localeCompare(b.title));
  }

  async function load() {
    const url = getFavouriteApiUrl();

    // eslint-disable-next-line compat/compat
    const res = await fetch(url);

    const data = await res.json();

    for (const link of data.links) {
      if (link.current === true) {
        isFavourites = link.isFavourites;
      }
    }

    data.charts.forEach((chart) => {
      chart.showFavouriteButton = true;
      chart.portal = isFavourites
        ? favouriteCharts[chart.id]
        : {
            categoryId: data.id,
            title: data.title,
            rowspan: chart.rowspan,
            colspan: chart.colspan,
          };
    });

    calculateFavourites();
    manifest = data;
  }

  async function gotoLink(e, link) {
    e.preventDefault();

    datasource = link.endpoint;
    isFavourites = link.isFavourites;
    await load();
    const clonedManifest = JSON.parse(JSON.stringify(manifest));
    window.history.pushState(clonedManifest, '', link.href);
  }

  function getChartFromManifest(id) {
    return manifest.charts.find((chart) => chart.id === id);
  }

  function getCategoryTitle(id) {
    return manifest.links.find((dataset) => dataset.guid === id)?.title;
  }

  window.addEventListener('popstate', (e) => {
    manifest = e.state;
  });

  load();

  let shareableLink = $derived(location.href + '?f=' + serializeFavourites());

  // Favourites Drag-And-Drop
  function onDragStart(category) {
    category.snapshot = category.charts.slice();
  }

  function onDragOver(category, event) {
    category.charts = move(category.charts, event);
  }

  function onDragEnd(category, event) {
    if (event.canceled) {
      category.charts = category.snapshot;
    } else {
      const favs = getFavourites();
      const cat = favs[category.id];
      const charts = { ...cat.charts };
      cat.charts = {};

      for (const chart of category.charts) {
        cat.charts[chart.id] = charts[chart.id];
      }

      updateFavourites(favs);
      shareableLink = location.href + '?f=' + serializeFavourites();
    }
  }
</script>

<div class="dynamic-data-portal">
  {#if manifest}
    <ul>
      {#each manifest.links as link, i (i)}
        <li>
          <a href="?e={link.endpoint}" onclick={(e) => gotoLink(e, link)}>
            {link.title}
          </a>
        </li>
      {/each}
    </ul>

    <h1>{manifest.title}</h1>

    {#if isFavourites}
      {#each favouriteCategories as category (category.id)}
        <h2>{getCategoryTitle(category.id)}</h2>
        <DateFilterBar bind:dateRange={category.dateRange} />
        <!-- <div>{category.dateRange.min} - {category.dateRange.max}</div> -->
        <DragDropProvider
          onDragStart={() => onDragStart(category)}
          onDragOver={(event) => onDragOver(category, event)}
          onDragEnd={(event) => onDragEnd(category, event)}
        >
          <div class="data-portal dynamic-grid-layout" data-grid-columns="2">
            {#each category.charts as portalChart (portalChart.id)}
              {@const chart = getChartFromManifest(portalChart.id)}
              <SortableChart
                {chart}
                {category}
                {portalChart}
                {calculateFavourites}
              />
            {/each}
          </div>
        </DragDropProvider>
      {/each}
      <p><a href={shareableLink}>Shareable Link</a></p>
    {:else}
      <DateFilterBar bind:dateRange />

      <div class="data-portal dynamic-grid-layout" data-grid-columns="2">
        {#each manifest.charts as chart (chart.id)}
          {@const Component = chart.config.type === 'table' ? Datatable : Chart}

          <div
            class="grid-item"
            data-item-colspan={chart.colspan || 1}
            data-item-rowspan={chart.rowspan || 1}
          >
            <Component {...chart} bind:dateRange />
          </div>
        {/each}
      </div>
    {/if}
    <!-- <pre><code>{JSON.stringify(manifest)}</code></pre> -->
  {:else}
    Loading...
  {/if}
</div>
